import clsx from 'clsx';
import React from 'react';

// One selectable metric tile in the Account Overview radiogroup (ENG-5256).
// Renders a metric label and its big live value (teal when selected). Avg Rating
// passes value=null → renders "—" so a stale number is never shown beside a
// fresh Reviewed count. The per-metric lens line lives on the chart heading, not
// the card.
//
// Selection system (no inline style):
// - resting: surface-secondary + hairline
// - hover: surface-muted (120ms)
// - selected: 2px teal inset ring + teal number, radius 6
// - focus: blue (#2389C1) offset outline, :focus-visible-gated
// Selected (teal inset) and focused (blue offset outline) coexist and stay
// visually distinct because they use different box properties (ring vs outline).

interface StatCardProps {
  /** Metric label, echoed verbatim by the chart heading. */
  label: string;
  /** Pre-formatted display value. `null` (Avg Rating, no ratings) renders "—". */
  value: string | null;
  /** Glyph rendered to the right of the value (Avg Rating's ★). Omitted when value is null. */
  valueSuffix?: string;
  selected: boolean;
  /** Roving-radiogroup props from useRovingRadioGroup (role, tabIndex, handlers, ref). */
  optionProps: React.HTMLAttributes<HTMLButtonElement> & {
    ref: (node: HTMLElement | null) => void;
    role: 'radio';
    tabIndex: 0 | -1;
    'aria-checked': boolean;
  };
}

export function StatCard({
  label,
  value,
  valueSuffix,
  selected,
  optionProps,
}: StatCardProps): React.ReactNode {
  return (
    <button
      type="button"
      {...optionProps}
      className={clsx(
        'flex h-full w-full flex-col items-center justify-center gap-4 rounded-md p-12 text-center transition-colors duration-150',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-main',
        selected
          ? 'bg-surface-card ring-2 ring-inset ring-brand-primary'
          : 'border border-surface-border bg-surface-secondary hover:bg-surface-muted'
      )}
    >
      <span
        className={clsx(
          'text-24 font-semibold tabular-nums leading-tight',
          selected ? 'text-brand-primary' : 'text-on-surface'
        )}
      >
        {value !== null && valueSuffix ? (
          <>
            <span>{value}</span>
            <span className="ml-2">{valueSuffix}</span>
          </>
        ) : (
          (value ?? '—')
        )}
      </span>
      <span className="text-12 font-medium leading-snug text-on-surface-muted">{label}</span>
    </button>
  );
}
